import { useState } from 'react';

export default function ColorConverter() {
    const [color, setColor] = useState('#3b82f6');
    const [formats, setFormats] = useState({
        hex: '#3b82f6',
        rgb: 'rgb(59, 130, 246)',
        hsl: 'hsl(217, 91%, 60%)',
        rgba: 'rgba(59, 130, 246, 1)',
        hsla: 'hsla(217, 91%, 60%, 1)',
    });

    const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1]!, 16),
            g: parseInt(result[2]!, 16),
            b: parseInt(result[3]!, 16)
        } : null;
    };

    const rgbToHsl = (r: number, g: number, b: number) => {
        r /= 255;
        g /= 255;
        b /= 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0, s = 0;
        const l = (max + min) / 2;

        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

            switch (max) {
                case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
                case g: h = ((b - r) / d + 2) / 6; break;
                case b: h = ((r - g) / d + 4) / 6; break;
            }
        }

        return {
            h: Math.round(h * 360),
            s: Math.round(s * 100),
            l: Math.round(l * 100)
        };
    };

    const updateColor = (newColor: string) => {
        setColor(newColor);
        const rgb = hexToRgb(newColor);
        if (rgb) {
            const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
            setFormats({
                hex: newColor,
                rgb: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
                hsl: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`,
                rgba: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`,
                hsla: `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, 1)`,
            });
        }
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            alert('Copied to clipboard!');
        } catch (e) {
            alert('Failed to copy');
        }
    };

    const presetColors = [
        '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
        '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
    ];

    return (
        <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-2">Color Converter</h1>
            <p className="text-gray-600 mb-6">Convert between HEX, RGB, and HSL color formats</p>

            <div className="tool-card mb-6">
                <div className="flex gap-6 items-start">
                    <div className="flex-1">
                        <label className="font-medium block mb-2">Pick a Color</label>
                        <div className="flex gap-4 items-center">
                            <input
                                type="color"
                                value={color}
                                onChange={(e) => updateColor(e.target.value)}
                                className="w-24 h-24 rounded-lg cursor-pointer border-2 border-gray-300"
                            />
                            <div className="flex-1">
                                <input
                                    type="text"
                                    value={color}
                                    onChange={(e) => updateColor(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono"
                                    placeholder="#3b82f6"
                                />
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {presetColors.map((preset) => (
                                        <button
                                            key={preset}
                                            onClick={() => updateColor(preset)}
                                            className="w-8 h-8 rounded border-2 border-gray-300 hover:scale-110 transition-transform"
                                            style={{ backgroundColor: preset }}
                                            title={preset}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                {Object.entries(formats).map(([format, value]) => (
                    <div key={format} className="tool-card">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-semibold text-lg uppercase">{format}</h3>
                            <button
                                onClick={() => copyToClipboard(value)}
                                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                            >
                                Copy
                            </button>
                        </div>
                        <div className="flex items-center gap-3">
                            <div
                                className="w-12 h-12 rounded border-2 border-gray-300"
                                style={{ backgroundColor: color }}
                            />
                            <code className="flex-1 p-3 rounded bg-gray-50 border border-gray-200 text-sm font-mono">
                                {value}
                            </code>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold mb-2">💡 Color Format Guide</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                    <li><strong>HEX:</strong> Hexadecimal notation (#RRGGBB)</li>
                    <li><strong>RGB:</strong> Red, Green, Blue values (0-255)</li>
                    <li><strong>HSL:</strong> Hue (0-360°), Saturation (0-100%), Lightness (0-100%)</li>
                    <li><strong>RGBA/HSLA:</strong> Same as RGB/HSL with Alpha (opacity) channel</li>
                </ul>
            </div>
        </div>
    );
}
